import React, { ReactNode } from 'react';
import {
    TextField,
    EditButton,
    ReferenceField,
    Filter,
    TextInput,
    SelectInput,
    ReferenceInput,
} from 'react-admin';
import CustomizableGrid from '../components/CustomizableGrid';
import { ColumnConfig } from '../hooks/useColumnReorder';

import CountryField from '../components/CountryField';
import CustomSearchInput from '../components/CustomSearchInput';

const AffiliateFilter = props => (
    <Filter variant="outlined" {...props}>
        <CustomSearchInput alwaysOn />
        <ReferenceInput
            source="countryId"
            reference="country"
            resource="country"
            label="Country"
        >
            <SelectInput optionText={choice => `${choice.name}`} />
        </ReferenceInput>
    </Filter>
);

const AffiliateList = ({ permissions = [], ...props }) => {
    // Define default columns configuration
    const defaultColumns: ColumnConfig[] = [
        { id: 'serial', label: 'Serial', visible: true, order: 0 },
        { id: 'country', label: 'Country', visible: true, order: 1 },
        { id: 'id', label: 'ID', visible: true, order: 2 },
        { id: 'name', label: 'Name', visible: true, order: 3 },
        {
            id: 'commissionsPercent',
            label: 'Commission (%)',
            visible: true,
            order: 4,
        },
        { id: 'user', label: 'User', visible: true, order: 5 },
        { id: 'edit', label: 'Edit', visible: true, order: 6 },
    ];

    // Function to render column based on column ID
    const renderColumn = (columnId: string): ReactNode => {
        switch (columnId) {
            case 'serial':
                return (
                    <TextField
                        key={columnId}
                        source="serial"
                        sortable={false}
                    />
                );
            case 'country':
                return <CountryField key={columnId} />;
            case 'id':
                return <TextField key={columnId} source="id" />;
            case 'name':
                return <TextField key={columnId} source="name" />;
            case 'commissionsPercent':
                return (
                    <TextField
                        key={columnId}
                        source="commissionsPercent"
                        label="Commission (%)"
                    />
                );
            case 'user':
                return (
                    <ReferenceField
                        key={columnId}
                        label="User"
                        source="userId"
                        reference="users"
                        link="show"
                    >
                        <TextField source="name" />
                    </ReferenceField>
                );
            case 'edit':
                return <EditButton key={columnId} color="secondary" />;
            default:
                return null;
        }
    };

    // Ensure we have all required props, but avoid duplicates with what might be in props
    const combinedProps = {
        title: 'Affiliates',
        permissions,
        defaultColumns,
        storageKey: 'affiliateListColumns',
        renderColumn,
        filtersComponent: <AffiliateFilter />,
        sort: { field: 'id', order: 'ASC' },
        ...props,
        // Ensure these are set if not in props
        resource: props.resource || 'affiliates',
        location: props.location || { search: '' },
        hasShow: props.hasShow !== undefined ? props.hasShow : false,
    };

    return <CustomizableGrid {...combinedProps} />;
};

AffiliateList.defaultProps = {
    resource: 'affiliates',
    location: { search: '' },
};

export default AffiliateList;
